import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getInitialDashboardLocale, type DashboardLocale } from '@/lib/localeStorage';

/**
 * Dynamic loaders so Vite ships each locale as a separate chunk and the boot bundle only
 * carries the one the user actually starts in (~100 KB saved for a single-locale visit).
 * The other locale loads on demand the first time `setDashboardLanguage` switches to it.
 */
const loaders: Record<DashboardLocale, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en.json'),
  el: () => import('./locales/el.json'),
};

const initialLocale = getInitialDashboardLocale();

// Sync the document language BEFORE React first renders. Modern browsers use the
// `lang` attribute when applying CSS `text-transform: uppercase`, which is the
// standard way to strip the Greek tonos (΄) on uppercase letters
// (e.g. "Λειτουργίες" → "ΛΕΙΤΟΥΡΓΙΕΣ" instead of "ΛΕΙΤΟΥΡΓΊΕΣ"). Setting it later
// in a useEffect causes a one-frame flash where uppercase text renders with tonos.
if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
}

/**
 * Resolved once the initial locale's resource bundle is loaded and i18next is initialised.
 * `main.tsx` awaits this before mounting React so the first render has translations.
 */
export const i18nReady: Promise<typeof i18n> = (async () => {
  const initial = await loaders[initialLocale]();
  await i18n.use(initReactI18next).init({
    resources: { [initialLocale]: { translation: initial.default } },
    lng: initialLocale,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });
  return i18n;
})();

i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng.startsWith('el') ? 'el' : 'en';
  }
});

/**
 * Sync-from-caller API kept for compatibility with existing call sites. Internally lazy-loads
 * the target locale's resource bundle on first switch, then changes language.
 */
export function setDashboardLanguage(locale: DashboardLocale): void {
  void (async () => {
    if (!i18n.hasResourceBundle(locale, 'translation')) {
      try {
        const r = await loaders[locale]();
        i18n.addResourceBundle(locale, 'translation', r.default);
      } catch (e) {
        console.error('Failed to load locale chunk:', locale, e);
        return;
      }
    }
    await i18n.changeLanguage(locale);
  })();
}

export { i18n };
